import React, { useMemo } from "react";
import {
  getActiveInnings,
  getBattingRows,
  getBowlingRows,
  getFallOfWickets,
  getRunsPerOver,
} from "../../utils/publicScorecardAdapter";
import { formatPlayerName } from "../../utils/nameFormatter";

interface TeamScorecardTabProps {
  matchData: any;
  teamSide: "A" | "B";
  teamName: string;
}

function formatOvers(totalBalls: number, bpo: number): string {
  const overs = Math.floor(totalBalls / bpo);
  const balls = totalBalls % bpo;
  return balls > 0 ? `${overs}.${balls}` : `${overs}`;
}

export default function TeamScorecardTab({ matchData, teamSide, teamName }: TeamScorecardTabProps) {
  const inningsData = useMemo(() => {
    if (!matchData?.innings) return [];
    const bpo = matchData.ballsPerOver || 6;
    const activeInnings = getActiveInnings(matchData);

    const format = matchData?.format;
    return activeInnings
      .filter((inn: any) => inn.battingTeam === teamSide)
      .map((inn: any) => {
        const battingPlayers = teamSide === "A" ? matchData.teamAPlayers : matchData.teamBPlayers;
        const bowlingPlayers = teamSide === "A" ? matchData.teamBPlayers : matchData.teamAPlayers;
        return {
          innings: inn,
          battingRows: getBattingRows(inn, battingPlayers, format),
          bowlingRows: getBowlingRows(inn, bowlingPlayers, bpo),
          fallOfWickets: getFallOfWickets(inn, battingPlayers, bpo, format),
          runsPerOver: getRunsPerOver(inn),
          battingPlayers,
          bpo,
        };
      });
  }, [matchData, teamSide]);

  if (inningsData.length === 0) {
    return (
      <div className="bg-slate-800/40 rounded-xl border border-slate-700/50 p-6 text-center">
        <p className="text-slate-400 text-sm">No innings data available yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {inningsData.map((data, idx) => {
        const inn = data.innings;
        const totalBalls = inn.totalBalls || 0;
        const oversText = formatOvers(totalBalls, data.bpo);
        const extras = inn.extras;
        const totalExtras = extras ? extras.wides + extras.noBalls + extras.byes + extras.legByes : 0;

        return (
          <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-slate-700 shadow-xl overflow-hidden">
            {/* Innings header */}
            <div className="px-4 py-3 border-b border-slate-700/60 bg-slate-900/40">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-white">{teamName} Innings</h3>
                <div className="text-emerald-400 font-black text-xl tabular-nums">
                  {inn.totalRuns || 0}/{inn.wickets || 0}
                  <span className="text-slate-400 text-sm font-normal ml-2">({oversText})</span>
                </div>
              </div>
            </div>

            <div className="p-4 space-y-5">
              {/* Batting table */}
              <div>
                <h4 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-2">Batting</h4>
                {data.battingRows.length > 0 ? (
                  <div className="overflow-x-auto rounded border border-slate-700 -mx-px">
                    <table className="w-full text-sm min-w-[340px]">
                      <thead className="bg-slate-900">
                        <tr>
                          <th className="text-left p-2 font-semibold whitespace-nowrap">Batter</th>
                          <th className="text-left p-2 font-semibold whitespace-nowrap hidden sm:table-cell">Dismissal</th>
                          <th className="text-right p-2 font-semibold">R</th>
                          <th className="text-right p-2 font-semibold">B</th>
                          <th className="text-right p-2 font-semibold">4s</th>
                          <th className="text-right p-2 font-semibold">6s</th>
                          <th className="text-right p-2 font-semibold">SR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.battingRows.map((b: any, i: number) => (
                          <tr key={i} className="odd:bg-slate-900/50">
                            <td className="p-2">
                              <div className="flex items-center gap-1">
                                <span className="font-semibold text-white truncate">{formatPlayerName(b.name)}</span>
                                {b.isCaptain && <span className="text-amber-400 text-xs flex-shrink-0">(C)</span>}
                                {b.isKeeper && <span className="text-sky-400 text-xs flex-shrink-0">(WK)</span>}
                              </div>
                              <div className="text-xs text-slate-400 leading-tight sm:hidden mt-0.5">{b.howOut}</div>
                            </td>
                            <td className="p-2 hidden sm:table-cell">
                              <span className="text-xs text-slate-400">{b.howOut}</span>
                            </td>
                            <td className="p-2 text-right font-bold text-white tabular-nums">{b.runs}</td>
                            <td className="p-2 text-right text-slate-300 tabular-nums">{b.balls}</td>
                            <td className="p-2 text-right text-slate-300 tabular-nums">{b.fours}</td>
                            <td className="p-2 text-right text-slate-300 tabular-nums">{b.sixes}</td>
                            <td className="p-2 text-right text-slate-300 tabular-nums">{b.strikeRate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm py-2">No batting data yet</p>
                )}
              </div>

              {/* Extras */}
              {extras && totalExtras > 0 && (
                <div className="text-sm text-slate-400">
                  <span className="font-semibold text-slate-300">Extras:</span> {totalExtras}
                  <span className="text-slate-500"> (Wd {extras.wides}, Nb {extras.noBalls}, B {extras.byes}, Lb {extras.legByes})</span>
                </div>
              )}

              {/* Fall of Wickets */}
              {data.fallOfWickets.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-2">Fall of Wickets</h4>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    {data.fallOfWickets.map((fow: any) => (
                      <span key={fow.order} className="text-slate-400">
                        <span className="text-slate-500">{fow.order}.</span>
                        <span className="text-white font-semibold ml-1">{fow.score}-{fow.wicket}</span>
                        <span className="ml-1">({formatPlayerName(fow.batterName)}, {fow.over} ov)</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Innings total */}
              <div className="flex items-center justify-between border-t border-slate-700/60 pt-3">
                <span className="text-sm font-semibold text-slate-300">Total</span>
                <span className="text-lg font-black text-emerald-400 tabular-nums">
                  {inn.totalRuns || 0}/{inn.wickets || 0}
                  <span className="text-slate-400 text-sm font-normal ml-2">({oversText} overs)</span>
                </span>
              </div>

              {/* Bowling table */}
              {data.bowlingRows.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-2">Bowling</h4>
                  <div className="overflow-x-auto rounded border border-slate-700 -mx-px">
                    <table className="w-full text-sm min-w-[300px]">
                      <thead className="bg-slate-900">
                        <tr>
                          <th className="text-left p-2 font-semibold whitespace-nowrap">Bowler</th>
                          <th className="text-right p-2 font-semibold">O</th>
                          <th className="text-right p-2 font-semibold">M</th>
                          <th className="text-right p-2 font-semibold">R</th>
                          <th className="text-right p-2 font-semibold">W</th>
                          <th className="text-right p-2 font-semibold">Econ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.bowlingRows.map((b: any, i: number) => (
                          <tr key={i} className="odd:bg-slate-900/50">
                            <td className="p-2">
                              <div className="flex items-center gap-1">
                                <span className="font-semibold text-white truncate">{formatPlayerName(b.name)}</span>
                                {b.isCaptain && <span className="text-amber-400 text-xs flex-shrink-0">(C)</span>}
                                {b.isKeeper && <span className="text-sky-400 text-xs flex-shrink-0">(WK)</span>}
                              </div>
                            </td>
                            <td className="p-2 text-right text-slate-300 tabular-nums">{b.overs}</td>
                            <td className="p-2 text-right text-slate-300 tabular-nums">{b.maidens}</td>
                            <td className="p-2 text-right text-slate-300 tabular-nums">{b.runs}</td>
                            <td className="p-2 text-right font-bold text-white tabular-nums">{b.wickets}</td>
                            <td className="p-2 text-right text-slate-300 tabular-nums">{b.economy}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
